import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import Card from "../ui/Card";
import Button from "../ui/Button";
import { useAuth } from "../../context/AuthContext";

type SellerSidebarProps = {
  collapsed: boolean;
};

type SellerSidebarItem = {
  label: string;
  to: string;
  icon: string;
  end?: boolean;
};

const sellerSidebarItems: SellerSidebarItem[] = [
  {
    label: "Inicio",
    to: "/seller",
    icon: "◫",
    end: true,
  },
  {
    label: "Envíos",
    to: "/seller/envios",
    icon: "≣",
  },
  {
    label: "Nuevo envío",
    to: "/seller/envios/nuevo",
    icon: "+",
  },
  {
    label: "Impresión de etiquetas",
    to: "/seller/envios/impresion",
    icon: "▤",
  },
];

const sidebarStyle = (collapsed: boolean): React.CSSProperties => ({
  width: collapsed ? "78px" : "244px",
  minWidth: collapsed ? "78px" : "244px",
  height: "100vh",
  position: "sticky",
  top: 0,
  borderRight: "1px solid var(--border)",
  background: "var(--surface)",
  backdropFilter: "var(--glass)",
  WebkitBackdropFilter: "var(--glass)",
  boxShadow: "var(--shadow)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  transition: "width var(--transition-base), min-width var(--transition-base)",
  zIndex: 30,
});

const topSectionStyle: React.CSSProperties = {
  padding: "16px 12px 10px 12px",
};

const brandRowStyle = (collapsed: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: collapsed ? "center" : "flex-start",
  gap: "12px",
  minHeight: "48px",
});

const logoBoxStyle: React.CSSProperties = {
  width: "42px",
  height: "42px",
  borderRadius: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--primary-soft)",
  border: "1px solid var(--border)",
  color: "var(--primary)",
  fontWeight: 800,
  fontSize: "16px",
  letterSpacing: "0.06em",
  flexShrink: 0,
};

const brandMetaStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
};

const brandEyebrowStyle: React.CSSProperties = {
  fontSize: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "var(--text-secondary)",
};

const brandNameStyle: React.CSSProperties = {
  fontSize: "15px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const navStyle: React.CSSProperties = {
  padding: "8px 8px 0 8px",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
};

const getItemBaseStyle = ({
  active,
  collapsed,
}: {
  active: boolean;
  collapsed: boolean;
}): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: collapsed ? "center" : "space-between",
  gap: "10px",
  minHeight: "46px",
  padding: collapsed ? "0" : "0 14px",
  borderRadius: "18px",
  textDecoration: "none",
  color: active ? "var(--text-primary)" : "var(--text-secondary)",
  background: active ? "rgba(198,165,106,0.10)" : "transparent",
  border: active ? "1px solid var(--border)" : "1px solid transparent",
  transition: "all var(--transition-base)",
  fontWeight: active ? 700 : 600,
  fontSize: "14px",
});

const leftRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  minWidth: 0,
};

const iconWrapStyle: React.CSSProperties = {
  width: "18px",
  textAlign: "center",
  fontSize: "15px",
  flexShrink: 0,
};

const labelStyle: React.CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const bottomSectionStyle: React.CSSProperties = {
  padding: "12px 8px 14px 8px",
};

const helperCardTitleStyle: React.CSSProperties = {
  fontSize: "14px",
  fontWeight: 800,
  color: "var(--text-primary)",
  marginBottom: "6px",
};

const helperCardTextStyle: React.CSSProperties = {
  fontSize: "12px",
  lineHeight: 1.45,
  color: "var(--text-secondary)",
  marginBottom: "12px",
};

const compactInfoStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
};

const compactChipStyle: React.CSSProperties = {
  minHeight: "34px",
  borderRadius: "12px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-secondary)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "11px",
  fontWeight: 700,
  padding: "0 8px",
};

const getInitials = (displayName?: string | null, username?: string | null): string => {
  const source = (displayName || username || "").trim();

  if (!source) return "SL";

  const parts = source.split(/\s+/).filter(Boolean);

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

const SellerSidebar = ({ collapsed }: SellerSidebarProps) => {
  const navigate = useNavigate();
  const { displayName, username } = useAuth();
  const initials = getInitials(displayName, username);

  const handleOpenBilling = () => {
    navigate("/seller");

    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("seller:open-billing-modal"));
    }, 100);
  };

  return (
    <aside style={sidebarStyle(collapsed)}>
      <div>
        <div style={topSectionStyle}>
          <div style={brandRowStyle(collapsed)}>
            <div style={logoBoxStyle}>{initials}</div>

            {!collapsed ? (
              <div style={brandMetaStyle}>
                <span style={brandEyebrowStyle}>Lynx Platform</span>
                <span style={brandNameStyle}>Seller</span>
              </div>
            ) : null}
          </div>
        </div>

        <nav style={navStyle}>
          {sellerSidebarItems.map((item) => (
            <NavLink key={`${item.to}-${item.label}`} to={item.to} end={item.end}>
              {({ isActive }) => (
                <div style={getItemBaseStyle({ active: isActive, collapsed })}>
                  <div style={leftRowStyle}>
                    <span style={iconWrapStyle}>{item.icon}</span>
                    {!collapsed ? <span style={labelStyle}>{item.label}</span> : null}
                  </div>

                  {!collapsed && isActive ? <span>•</span> : null}
                </div>
              )}
            </NavLink>
          ))}
        </nav>
      </div>

      <div style={bottomSectionStyle}>
        {!collapsed ? (
          <Card style={{ padding: "16px" }}>
            <div style={helperCardTitleStyle}>Panel seller</div>

            <div style={helperCardTextStyle}>
              Accedé al resumen de facturación y detalle disponible en legacy.
            </div>

            <Button variant="secondary" fullWidth onClick={handleOpenBilling}>
              Ver resumen
            </Button>
          </Card>
        ) : (
          <div style={compactInfoStyle}>
            <div style={compactChipStyle}>SL</div>
            <div style={compactChipStyle}>MVP</div>
          </div>
        )}
      </div>
    </aside>
  );
};

export default SellerSidebar;